import React from "react"
import {
  CalendarEventStoreType,
  CalendarEventSnapshotType,
  CalendarEventDayType,
} from "modules/main/models/calendar-event-store"
import { observer } from "mobx-react"
import styles from "./Calendar.module.scss"
import { CalendarWidget } from "./CalendarWidget"
import { DayEvents } from "./DayEvents"
import { PeriodSliderGray } from "modules/common/components/period/PeriodSliderGray"
import { EventEditor } from "./EventEditor"
import { SessionType } from "modules/session/auth/models/session"
import { getSnapshot } from "mobx-state-tree"
import { TimesheetRow } from "./TimeSheet"
import { QuestionsTab } from "modules/order-questions/components/Tab"
import { OrderQuestionStoreType } from "modules/order-questions/models/store"
import { UserPendingSignaturesStoreType } from "modules/main/models/order-content-task-signatures"

import { MainPageRequestsStoreType } from "modules/main/models/main-page-requests"
import { RequestList } from "../request-list/RequestList"
import { NotificationStoreType } from "modules/root/models/alerts"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { SignaturesList } from '../signatures-list/SignaturesList'
import { EmployeeSignaturesListType } from '../signatures-list/models/employee-signatures'

const localTabName = "MainPageTab"

/**
 *  Вкладки виджета. Номера сохранены в localStorage у пользователей,
 *  поэтому новая вкладка получает следующий свободный номер, а не место в ряду
 */
const tabs = {
  calendar: 0,
  tasks: 1,
  signatures: 2,
  requests: 3,
}

export const Calendar = observer(
  class extends React.Component<CalendarProps, CalendarState> {
    constructor(props: CalendarProps) {
      super(props)
      this.state = { selection: "", creation: null, tabIndex: 0 }
    }

    componentDidMount() {
      this.props.calendar.employee.load({
        departmentId: "",
        withSalary: null,
        sortByHierarchy: true,
        withFired: false,
      })
      this.props.calendar.load()

      const now = new Date()
      this.props.calendar.timesheet.load(now.getFullYear(), now.getMonth() + 1)

      this.props.signaturesList.loadPendingSignatures({})

      // счётчик на кнопке «Запросы» нужен до того, как вкладку откроют
      this.props.mainPageRequests.load()

      let local = localStorage.getItem(localTabName)
      try {
        if (local) {
          this.setState({ tabIndex: +JSON.parse(local) })
        }
      } catch (e) {
        /*Ignore*/
      }
    }
    onRemove = (e: CalendarEventDayType, forAll: boolean) => this.props.calendar.remove(e.event.eventGuid, forAll);
    render() {
      const {
        calendar: store,
        session,
        canComment,
        orderQuestions,
        canManageQuestions,
        canReadQuestions,
        mainPageRequests,
        notification,
        baseUrl,
        signaturesList,
        ownUserSignaturesList,
      } = this.props
      const { selection, creation, tabIndex } = this.state

      const requestsCount = mainPageRequests.count

      // вкладка запросов нужна тем, кто их решает, — даже когда решать нечего.
      // Права хватает не всегда: ГИП подтверждает версии проектов без него,
      // поэтому решающего называет сервер
      const requestsVisible = mainPageRequests.visible || requestsCount > 0

      // вкладки видны не всем: задания закрыты правом доступа. Скрытую вкладку
      // не показываем, а выбранную — возвращаем к календарю
      const hidden =
        (tabIndex === tabs.requests && !requestsVisible) || (tabIndex === tabs.tasks && !canReadQuestions)

      const currentTab = hidden ? tabs.calendar : tabIndex

      const day = store.daysAsMap[selection]
      const events = store.eventsAsMap.get(selection) || []

      return (
        <>
          <div className={`${styles.calendar} main-widget`}>
            <div className={` flat-mode ${styles.header}`}>
              <PeriodSliderGray year={store.year} month={store.month} onChange={this.gotoMonth} />
            </div>

            <TimesheetRow
              store={store.timesheet}
              userId={session.userId}
              userLogin={session.userLogin}
              canComment={canComment}
            />
          </div>
          <div className={styles.tabButtons}>
            <PlanrButton
              type={currentTab === tabs.calendar ? "blueish" : "lightblueish"}
              size="small"
              onClick={() => this.selectTab(tabs.calendar)}
            >
              Календарь
            </PlanrButton>
            {canReadQuestions && (
              <PlanrButton
                type={currentTab === tabs.tasks ? "blueish" : "lightblueish"}
                size="small"
                onClick={() => this.selectTab(tabs.tasks)}
              >
                Задания
              </PlanrButton>
            )}
            {requestsVisible && (
              <div style={{ position: "relative", display: "inline-block", marginLeft: "8px" }}>
                <PlanrButton
                  type={currentTab === tabs.requests ? "blueish" : "lightblueish"}
                  size="small"
                  onClick={() => this.selectTab(tabs.requests)}
                >
                  Запросы
                </PlanrButton>
                {requestsCount > 0 && <div className={styles.badge}>{requestsCount}</div>}
              </div>
            )}
            <div style={{ position: "relative", display: "inline-block", marginLeft: "8px" }}>
              <PlanrButton
                type={currentTab === tabs.signatures ? "blueish" : "lightblueish"}
                size="small"
                onClick={() => this.selectTab(tabs.signatures)}
              >
                Подписи
              </PlanrButton>
              {signaturesList.pendingSignatures.length > 0 && (
                <div className={styles.badge}>{signaturesList.pendingSignatures.length}</div>
              )}
            </div>
          </div>
          <div className={`${styles.calendar} ${styles.content} main-widget`}>
            {currentTab === tabs.signatures && (
              <div className={styles.tasks}>
                <SignaturesList store={signaturesList} notification={notification} baseUrl={baseUrl} ownUserSignaturesList={ownUserSignaturesList} userId={session.userId} />
              </div>
            )}
            {currentTab === tabs.tasks && (
              <div className={styles.board}>
                {/* та же доска, что и на странице заданий, только уже: адрес страницы
                    она не трогает — задание открывается диалогом поверх главной */}
                <QuestionsTab
                  store={orderQuestions}
                  canManage={canManageQuestions}
                  canAddQuestion={canManageQuestions}
                  currentUserId={session.userId}
                  isActiveTab={true}
                  embedded={true}
                  compact={true}
                />
              </div>
            )}
            {currentTab === tabs.requests && (
              <div className={styles.tasks}>
                <RequestList store={mainPageRequests} />
              </div>
            )}
            {currentTab === tabs.calendar && (
              <CalendarWidget store={store} selection={selection} onSelectDay={this.onSelectDay} />
            )}

            {day && (
              <DayEvents
                day={day}
                events={events}
                onAdd={this.startCreation}
                onChange={this.startEdit}
                onRemove={this.onRemove}
              />
            )}
            {creation && (
              <EventEditor
                onClose={this.closeEditor}
                employee={store.employee}
                onSave={store.save}
                event={creation}
                currentUser={session.userId}
                width={700}
              />
            )}
          </div>
        </>
      )
    }

    selectTab = (tabIndex: number) => {
      localStorage.setItem(localTabName, JSON.stringify(tabIndex))
      // выбор дня относится только к календарю
      this.setState({ tabIndex, selection: tabIndex === tabs.calendar ? this.state.selection : "" })
    };

    onSelectDay = (day: string) => {
      if (day === this.state.selection) {
        day = ""
      }

      this.setState({ selection: day })
    };

    gotoMonth = (year: number, month: number) => {
      this.props.calendar.setPeriod(year, month)
      this.props.calendar.load()
      this.props.calendar.timesheet.load()
    };

    startCreation = (day: string) => {
      this.setState({ creation: this.props.calendar.factory(day) })
    };

    startEdit = (event: CalendarEventDayType) => {
      this.setState({ creation: getSnapshot(event.event) })
    };

    closeEditor = () => this.setState({ creation: null });
  }
)

interface CalendarProps {
  calendar: CalendarEventStoreType
  session: SessionType
  canComment: boolean
  orderQuestions: OrderQuestionStoreType
  /** Задания видно по праву доступа: без него вкладки «Задания» на главной нет */
  canReadQuestions: boolean
  canManageQuestions: boolean
  mainPageRequests: MainPageRequestsStoreType
  signaturesList: UserPendingSignaturesStoreType
  ownUserSignaturesList: EmployeeSignaturesListType
  notification: NotificationStoreType
  baseUrl: string
}

interface CalendarState {
  selection: string
  creation: CalendarEventSnapshotType | null
  tabIndex: number
}
